import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { getIconComponent } from '../utils/iconMapping';
import AddIconDialog from './AddIconDialog';

const SkeletonRows = ({ count = 6 }) => (
  Array.from({ length: count }).map((_, i) => (
    <TableRow key={i}>
      <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
      <TableCell><Skeleton width={140} /></TableCell>
      <TableCell><Skeleton width={100} /></TableCell>
      <TableCell align="center"><Skeleton width={40} sx={{ mx: 'auto' }} /></TableCell>
      <TableCell align="right"><Skeleton variant="circular" width={28} height={28} sx={{ ml: 'auto' }} /></TableCell>
    </TableRow>
  ))
);

const IconMappingPanel = ({
  iconMappings,
  loading,
  canManage,
  onRefresh,
  onAdd,
  onEdit,
  onDelete,
  saving,
  t
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const existingKeys = iconMappings.map(m => m.key);

  const handleAdd = (data) => {
    onAdd(data);
    setAddDialogOpen(false);
  };

  const handleEdit = (mapping) => {
    setEditMapping(mapping);
  };

  const handleEditSave = (data) => {
    onEdit(data);
    setEditMapping(null);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      onDelete(deleteConfirm.id);
      setDeleteConfirm(null);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {t ? t('QbusOutputTypes.icon_mappings_title') : 'Icon Mappings'}
          </Typography>
          {!loading && iconMappings.length > 0 && (
            <Chip label={iconMappings.length} size="small" color="primary" variant="outlined" />
          )}
        </Box>
        <Box>
          <Tooltip title={t ? t('Common.refresh') : 'Refresh'}>
            <IconButton onClick={onRefresh} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canManage && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
              size="small"
              sx={{ ml: 1 }}
            >
              {t ? t('QbusOutputTypes.add_icon') : 'Add Icon'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t ? t('QbusOutputTypes.icon') : 'Icon'}</TableCell>
                <TableCell>{t ? t('QbusOutputTypes.mui_key') : 'MUI Key'}</TableCell>
                <TableCell>{t ? t('QbusOutputTypes.icon_label') : 'Label'}</TableCell>
                <TableCell align="center">{t ? t('QbusOutputTypes.in_use') : 'In Use'}</TableCell>
                {canManage && <TableCell align="right" width={90}>{t ? t('Common.actions') : 'Actions'}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows />
              ) : iconMappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 5 : 4} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      {t ? t('QbusOutputTypes.no_icon_mappings') : 'No icon mappings found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                iconMappings.map((mapping) => {
                  const IconComp = getIconComponent(mapping.key);
                  const usageCount = parseInt(mapping.dataValues?.usageCount ?? mapping.usageCount ?? 0);
                  const isInUse = usageCount > 0;

                  return (
                    <TableRow key={mapping.id} hover>
                      <TableCell>
                        <IconComp color="primary" fontSize="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {mapping.key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{mapping.label}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isInUse ? (
                          <Chip
                            label={`${usageCount} type${usageCount !== 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            label={t ? t('QbusOutputTypes.unused') : 'Unused'}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell align="right">
                          <Tooltip title={t ? t('Common.edit') : 'Edit'}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(mapping)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={isInUse
                              ? (t ? t('QbusOutputTypes.cannot_delete_in_use') : `Cannot delete: used by ${usageCount} type(s)`)
                              : (t ? t('Common.delete') : 'Delete')
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setDeleteConfirm(mapping)}
                                disabled={isInUse}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Dialog */}
      <AddIconDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleAdd}
        existingKeys={existingKeys}
        saving={saving}
        t={t}
      />

      {/* Edit Dialog */}
      <AddIconDialog
        open={!!editMapping}
        onClose={() => setEditMapping(null)}
        onSave={handleEditSave}
        existingKeys={existingKeys}
        editMapping={editMapping}
        saving={saving}
        t={t}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>{t ? t('Common.are_you_sure') : 'Are you sure?'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t ? t('QbusOutputTypes.delete_icon_confirm') : 'Delete icon mapping'} <strong>{deleteConfirm?.label}</strong> ({deleteConfirm?.key})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>
            {t ? t('Common.cancel') : 'Cancel'}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t ? t('Common.delete') : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IconMappingPanel;
